import { useCallback, useEffect, useRef, useState } from 'react';
import './activity.logs.scss';
import { FilterPopup, Input, Skeleton } from '@storybook';
import { DatePicker } from 'components';
import { API_URL } from 'constant';
import { useNetwork, useOutsideClick } from 'hooks';
import {
	filteringTagsState,
	OpenActivityLogState,
	SelectedActivityDateRangeState,
} from 'global-stores';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { getDate, getOffDate } from 'utils';

interface IActivityLogs {
	activeOnboarding: {
		_id: string;
		name: string;
	} | null;
}

interface ActivityLog {
	_id: string;
	createdAt: string;
	userName: string;
	action: string;
	profile?: string;
}

interface ActivityData {
	activities: Record<string, ActivityLog[]>;
	totalPages: number;
}
const activities = { '0': ['', '', '', ''], '1': ['', '', '', '', '', ''] };
const ActivityLogs = ({ activeOnboarding }: IActivityLogs) => {
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [showEditorFilter, setShowEditorFilter] = useState(false);
	const [currentPage, setCurrentpage] = useState(1);
	const [pageInput, setPageInput] = useState(
		String(currentPage).padStart(2, '0')
	);
	const editorRef = useRef<HTMLDivElement>(null);
	const didMountRef = useRef(false);
	const [activityData, setActivityData] = useState<ActivityData>({
		activities: {},
		totalPages: 1,
	});
	const selectedDateRang = useRecoilValue(SelectedActivityDateRangeState);
	const resetOpenActivityLogState = useResetRecoilState(OpenActivityLogState);
	const filteringTags = useRecoilValue(filteringTagsState);
	const resetFilteringTags = useResetRecoilState(filteringTagsState);

	const { get } = useNetwork();

	useOutsideClick(editorRef, () => {
		setShowEditorFilter(false);
	});

	const groupedByDate = useCallback((list: ActivityLog[]) => {
		const grouped = list.reduce(
			(acc: Record<string, ActivityLog[]>, item: ActivityLog) => {
				const date: string = item.createdAt.split('T')[0] || '';
				if (!acc[date]) {
					acc[date] = [];
				}
				acc[date]?.push(item);
				return acc;
			},
			{}
		);
		return grouped;
	}, []);

	const handleComplex = useCallback(async () => {
		if (!activeOnboarding || !activeOnboarding._id) return;

		setLoading(true);
		const params: {
			type: string;
			page: number;
			limit: number;
			startDate?: string;
			endDate?: string;
			nameRegex?: string;
		} = {
			type: 'activities',
			page: currentPage,
			limit: 10,
			startDate: getOffDate(selectedDateRang?.[0]?.startDate || '', 'start'),
			endDate: getOffDate(selectedDateRang?.[0]?.endDate || '', 'end'),
		};

		if (searchTerm) {
			params.nameRegex = searchTerm;
		}
		let query = '';
		if (filteringTags.editor?.length) {
			query = filteringTags.editor
				.map((id: string) => `&userIds=${id}`)
				.join('');
		}

		const queryString = new URLSearchParams(
			params as unknown as Record<string, string>
		).toString();
		const url = `${API_URL.COMPLEX_FLOW}/${activeOnboarding._id}?${queryString}${query}`;
		const { data, message } = await get(url);
		if (message === 'ok') {
			const activities = groupedByDate(data.activities);
			setActivityData({ activities, totalPages: data.totalPages });
		}
		setLoading(false);
	}, [
		activeOnboarding,
		currentPage,
		selectedDateRang,
		searchTerm,
		filteringTags.editor,
		get,
		groupedByDate,
	]);

	function formatDate(input: string): string {
		const date = new Date(input);
		if (isNaN(date.getTime())) return 'Invalid Date';
	
		const now = new Date();
	
		const isSameDay = (d1: Date, d2: Date) =>
			d1.getFullYear() === d2.getFullYear() &&
			d1.getMonth() === d2.getMonth() &&
			d1.getDate() === d2.getDate();
	
		// Extract the time + time zone abbreviation
		const formatTimeWithZone = (date: Date) => {
			const options: Intl.DateTimeFormatOptions = {
				hour: 'numeric',
				minute: '2-digit',
				hour12: true,
				timeZoneName: 'short',
			};
	
			return new Intl.DateTimeFormat(undefined, options).format(date);
		};
	
		const formatDateOnly = (date: Date) =>
			new Intl.DateTimeFormat(undefined, {
				year: 'numeric',
				month: 'numeric',
				day: 'numeric',
			}).format(date);
	
		if (isSameDay(date, now)) {
			return `Today at ${formatTimeWithZone(date)}`;
		}
	
		const yesterday = new Date(now);
		yesterday.setDate(now.getDate() - 1);
		if (isSameDay(date, yesterday)) {
			return `Yesterday at ${formatTimeWithZone(date)}`;
		}
	
		return `${formatDateOnly(date)} at ${formatTimeWithZone(date)}`;
	}
	

	const paginate = useCallback(
		(direction: 'next' | 'prev') => {
			setCurrentpage(prev => {
				let newPage = prev;
				if (direction === 'next' && prev < activityData.totalPages) {
					newPage = prev + 1;
				} else if (direction === 'prev' && prev > 1) {
					newPage = prev - 1;
				}

				if (newPage !== prev) {
					setPageInput(String(newPage).padStart(2, '0'));
				}

				return newPage;
			});
		},
		[activityData.totalPages]
	);

	const handlePageInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if(+e.target.value <= activityData.totalPages){

				const raw = e.target.value.replace(/\D/g, '');
				setPageInput(raw);
			}
		},
		[activityData.totalPages]
	);

	useEffect(() => {
		setPageInput(String(currentPage));
	}, [currentPage]);

	useEffect(() => {
		const timeout = setTimeout(() => {
			const parsed = parseInt(pageInput, 10);
			if (
				!isNaN(parsed) &&
				parsed >= 1 &&
				parsed <= activityData.totalPages &&
				parsed !== currentPage
			) {
				setCurrentpage(parsed);
			}
		}, 500);

		return () => clearTimeout(timeout);
	}, [pageInput, activityData.totalPages, currentPage]);

	useEffect(() => {
		if (!didMountRef.current) return;
		if (currentPage !== 1) {
			setCurrentpage(1);
			return () => null;
		} else {
			const delayDebounce = setTimeout(() => {
				handleComplex();
			}, 300);

			return () => clearTimeout(delayDebounce);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchTerm, selectedDateRang, filteringTags?.editor]);

	useEffect(() => {
		if (activeOnboarding && activeOnboarding?._id) {
			const delayDebounce = setTimeout(() => {
				handleComplex();
			}, 300);

			return () => clearTimeout(delayDebounce);
		} else {
			resetOpenActivityLogState();
			return () => null;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPage, activeOnboarding]);

	useEffect(() => {
		didMountRef.current = true;
		return () => {
			resetFilteringTags();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<div className="ActivityLogs__header">
				<p className="ActivityLogs__heading">{activeOnboarding?.name}</p>
				<div className="ActivityLogs__filters">
					<Input
						className="ActivityLogs__filters-search"
						inputType="text"
						handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							setSearchTerm(e.target.value)
						}
						placeholder="Search"
						label=""
						suffixIcon="ri-search-line"
					/>
					<div
						className="ActivityLogs__filters-editors"
						ref={editorRef}
						onClick={() => setShowEditorFilter(true)}
					>
						<i className="ri-filter-3-line"></i>
						Filters
						{showEditorFilter && <FilterPopup filterPage="activity-logs" />}
					</div>
					<DatePicker stateType={SelectedActivityDateRangeState} />
					<div className="ActivityLogs__filters-pagination">
						<p>
							Page {currentPage} of {activityData.totalPages}
						</p>
						<button
							className={`ActivityLogs__filters-pagination-btn ${
								currentPage === 1 && 'disabled'
							}`}
							disabled={currentPage === 1}
							onClick={() => paginate('prev')}
						>
							<i className="ri-arrow-left-s-line"></i>
						</button>
						<Input
							className="ActivityLogs__filters-pagination-btn page"
							inputType="number"
							label=""
							placeholder=""
							value={
								pageInput === '0' || pageInput === ''
									? pageInput
									: pageInput.padStart(2, '0')
							}
							handleChange={handlePageInputChange}
						/>
						<button
							className={`ActivityLogs__filters-pagination-btn ${
								activityData.totalPages === currentPage && 'disabled'
							}`}
							disabled={activityData.totalPages === currentPage}
							onClick={() => paginate('next')}
						>
							<i className="ri-arrow-right-s-line"></i>
						</button>
					</div>
				</div>
			</div>
			<div className="ActivityLogs__body">
				{Object.entries(loading?activities:activityData.activities).map(([date, logs]) => (
					<div className="ActivityLogs__card" key={date}>
						<p className="ActivityLogs__card-heading">
							{loading ? (
								<Skeleton width={500} />
							) : (
								getDate(date, 'EEEE, MMMM dd, yyyy')
							)}
						</p>
						{logs.map((log: any) => (
							<div className="ActivityLogs__card-content" key={log?._id}>
								<div className="ActivityLogs__card-icon">
									{log?.profile ? (
										loading ? (
											<Skeleton width={38} height={38} borderRadius={50} />
										) : (
											<img src={log?.profile} height={'100%'} width={'100%'} />
										)
									) : loading ? (
										<Skeleton width={38} height={38} borderRadius={50} />
									) : (
										<i className="ri-user-shared-2-line"></i>
									)}
								</div>
								<div className="ActivityLogs__card-details">
									{loading ? (
										<Skeleton width={300} />
									) : (
										<p>
											{log?.userName} <b>{log?.action}</b> the onboarding flow.
										</p>
									)}
									{loading ? (
										<Skeleton width={150} height={10} />
									) : (
										<small>{formatDate(log?.createdAt)}</small>
									)}
								</div>
							</div>
						))}
					</div>
				))}
				{!Object.entries(activityData.activities)?.length && (
					<div className="ActivityLogs__body">
						<div className="ActivityLogs__body-no-record">No Record Found!</div>
					</div>
				)}
			</div>
		</>
	);
};

export default ActivityLogs;
